import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MagnifyingGlassIcon,
  XMarkIcon,
  BoltIcon,
  MapPinIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useTranslations } from '../hooks/useTranslations';
import { buildApiUrl } from '../config/environment';

interface ChargerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCharger: (charger: any) => void;
}

interface LocationSuggestion {
  display_name: string;
  lat: number;
  lng: number;
  type: string;
  importance: number;
}

interface SearchResult {
  id: number;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  numberOfPoints?: number;
  // Removed numberOfChargingPoints - use only numberOfPoints from OpenChargeMap API
  connections: any[];
  operatorId?: number;
}

const ChargerSearchModal: React.FC<ChargerSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectCharger
}) => {
  const { t } = useTranslations();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationSuggestion | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingChargers, setIsLoadingChargers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Step 1: Search for location suggestions with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setLocationSuggestions([]);
      setShowDropdown(false);
      setError(null);
      return;
    }

    if (searchQuery.length < 3) {
      setError(t('chargerSearch_min3chars'));
      setLocationSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      await searchLocations(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const searchLocations = async (query: string) => {
    setIsSearching(true);
    setError(null);

    try {
      const response = await fetch(
        buildApiUrl(`/api/chargers/locations?q=${encodeURIComponent(query)}`),
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('location search request failed');
      }

      const data = await response.json();
      setLocationSuggestions(data.locations || []);
      setShowDropdown(data.locations && data.locations.length > 0);
    } catch (err) {
      console.error('Location search error:', err);
      setError(t('chargerSearch_locationSearchFailed'));
      setLocationSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Step 2: Search for chargers around selected location
  const searchChargers = async (location: LocationSuggestion) => {
    setIsLoadingChargers(true);
    setError(null);
    setSelectedLocation(location);
    setShowDropdown(false);

    try {
      const response = await fetch(
        buildApiUrl(`/api/chargers/search?lat=${location.lat}&lng=${location.lng}&location_name=${encodeURIComponent(location.display_name)}`),
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('charger search request failed');
      }

      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (err) {
      console.error('Charger search error:', err);
      setError(t('chargerSearch_chargerSearchFailed'));
      setSearchResults([]);
    } finally {
      setIsLoadingChargers(false);
    }
  };

  const handleSelectCharger = (charger: SearchResult) => {
    onSelectCharger(charger);
    onClose();
    // Reset all state
    setSearchQuery('');
    setSearchResults([]);
    setLocationSuggestions([]);
    setSelectedLocation(null);
    setShowDropdown(false);
  };

  const getOperatorName = (operatorId?: number) => {
    const operatorNames: { [key: number]: string } = {
      23: 'Tesla',
      3534: 'Tesla',
      27: 'Tesla',
      3299: 'IONITY',
      56: 'eON',
      194: 'Vattenfall',
      25: 'POD Point'
    };
    return operatorNames[operatorId || 0] || t('chargerSearch_unknown');
  };

  const getOperatorColor = (operatorId?: number) => {
    const operatorColors: { [key: number]: string } = {
      23: 'bg-red-500',
      3534: 'bg-red-500',
      27: 'bg-red-500',
      3299: 'bg-purple-500',
      56: 'bg-blue-500',
      194: 'bg-yellow-500',
      25: 'bg-green-500'
    };
    return operatorColors[operatorId || 0] || 'bg-gray-500';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            data-testid="charger-search-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-full">
                    <BoltIcon className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">{t('chargerSearch_title')}</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Search Input */}
            <div className="p-6 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm transition-colors"
                  placeholder={t('chargerSearch_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="charger-search-input"
                />
                {isSearching && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-500"></div>
                  </div>
                )}
              </div>
              {searchQuery.length > 0 && searchQuery.length < 3 && (
                <p className="mt-2 text-sm text-amber-600 flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  {t('chargerSearch_typeMin3')}
                </p>
              )}
              
              {/* Location Suggestions Dropdown */}
              {showDropdown && locationSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto"
                >
                  {locationSuggestions.map((location, index) => (
                    <button
                      key={index}
                      onClick={() => searchChargers(location)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center space-x-3"
                    >
                      <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {location.display_name}
                        </div>
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Selected Location Display */}
            {selectedLocation && (
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <MapPinIcon className="h-4 w-4" />
                  <span>{t('chargerSearch_searchingAround')} <strong>{selectedLocation.display_name}</strong></span>
                  {isLoadingChargers && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500 ml-2"></div>
                  )}
                </div>
              </div>
            )}

            {/* Results */}
            <div className="flex-1 overflow-y-auto max-h-96" data-testid="charger-search-results">
              {error && (
                <div className="p-6 text-center">
                  <div className="text-red-500 text-sm bg-red-50 rounded-lg p-4">
                    {error}
                  </div>
                </div>
              )}

              {!error && searchQuery.length >= 3 && searchResults.length === 0 && !isSearching && (
                <div className="p-6 text-center text-gray-500">
                  <BoltIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('chargerSearch_noChargersFound')} "{searchQuery}"</p>
                  <p className="text-sm mt-1">{t('chargerSearch_tryDifferent')}</p>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="p-4 space-y-3">
                  {searchResults.map((charger) => (
                    <motion.div
                      key={charger.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gray-50 hover:bg-gray-100 rounded-xl p-4 transition-colors cursor-pointer border-l-4 border-emerald-500"
                      data-testid="charger-result-item"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Charger Name and Operator */}
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900" data-testid="charger-name">
                              {charger.name}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getOperatorColor(charger.operatorId)}`}>
                              {getOperatorName(charger.operatorId)}
                            </span>
                          </div>

                          {/* Address */}
                          <div className="flex items-center text-sm text-gray-600 mb-3" data-testid="charger-address">
                            <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span>{charger.address}</span>
                          </div>

                          {/* Charging Points */}
                          <div className="flex items-center space-x-4 mb-3">
                            <div className="flex items-center text-sm text-emerald-600 font-medium" data-testid="charger-points">
                              <BoltIcon className="h-4 w-4 mr-1" />
                              <span>{charger.numberOfPoints || t('chargerSearch_na')} {t('chargerSearch_chargingPoints')}</span>
                            </div>
                          </div>

                          {/* Connection Types */}
                          {charger.connections && charger.connections.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {charger.connections.slice(0, 3).map((conn, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                                >
                                  {conn.ConnectionType?.Title || t('chargerSearch_unknown')}
                                  {conn.PowerKW && ` (${conn.PowerKW}kW)`}
                                </span>
                              ))}
                              {charger.connections.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                                  +{charger.connections.length - 3} {t('chargerSearch_more')}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Select Button */}
                        <div className="ml-4">
                          <button
                            onClick={() => handleSelectCharger(charger)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                          >
                            {t('chargerSearch_selectCharger')}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {searchQuery.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <BoltIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">{t('chargerSearch_findPerfectStop')}</h3>
                  <p className="text-sm">
                    {t('chargerSearch_emptyStateDesc')}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default ChargerSearchModal;